<script lang="ts">
  import { Button, FormInput, FormCheckbox, RadioButtonGroup } from '../common';
  import type { LLMProvider, LLMConfigResponse } from '../../lib/api';

  type Props = {
    selectedProvider: LLMProvider;
    apiKey: string;
    model: string;
    ollamaUrl: string;
    optedIntoCloud: boolean;
    showApiKey: boolean;
    enabled: boolean;
    config: LLMConfigResponse | null;
    testing: boolean;
    testResult: { success: boolean; message?: string; error?: string } | null;
    onProviderChange: (provider: LLMProvider) => void;
    onTestConnection: () => void;
    onApiKeyChange: (value: string) => void;
    onModelChange: (value: string) => void;
    onOllamaUrlChange: (value: string) => void;
    onOptedIntoCloudChange: (value: boolean) => void;
    onShowApiKeyToggle: () => void;
  };

  let {
    selectedProvider,
    apiKey,
    model,
    ollamaUrl,
    optedIntoCloud,
    showApiKey,
    enabled,
    config,
    testing,
    testResult,
    onProviderChange,
    onTestConnection,
    onApiKeyChange,
    onModelChange,
    onOllamaUrlChange,
    onOptedIntoCloudChange,
    onShowApiKeyToggle
  }: Props = $props();

  const isCloudProvider = $derived(selectedProvider === 'anthropic' || selectedProvider === 'openai');
</script>

<div class="settings-grid">
  <!-- Provider Selection -->
  <div class="settings-card">
    <h3>AI Provider</h3>
    <RadioButtonGroup
      value={selectedProvider}
      options={[
        {
          value: 'ollama',
          label: 'Ollama (Local)',
          description: 'Run AI models locally on your computer. Maximum privacy, no data leaves your device. Free and open source.'
        },
        {
          value: 'anthropic',
          label: 'Anthropic Claude',
          description: 'High-quality categorization using Claude AI. Requires API key and internet connection. Data is sent to Anthropic servers.'
        },
        {
          value: 'openai',
          label: 'OpenAI GPT',
          description: 'Categorization using GPT models. Requires API key and internet connection. Data is sent to OpenAI servers.'
        }
      ]}
      name="provider"
      variant="card"
      layout="vertical"
      onchange={onProviderChange}
    />
  </div>

  <!-- Configuration based on provider -->
  <div class="settings-card">
    <h3>Configuration</h3>

    {#if selectedProvider === 'ollama'}
      <div class="form-group">
        <FormInput
          label="Ollama Server URL"
          type="text"
          value={ollamaUrl}
          onchange={(e) => onOllamaUrlChange(e.target.value)}
          placeholder="http://localhost:11434"
        />
        <p class="field-hint">
          Make sure Ollama is installed and running. <a href="https://ollama.ai" target="_blank" rel="noopener">Download Ollama</a>
        </p>
      </div>

      <div class="form-group">
        <FormInput
          label="Model Name"
          type="text"
          value={model}
          onchange={(e) => onModelChange(e.target.value)}
          placeholder="llama3"
        />
        <p class="field-hint">
          Recommended: llama3, llama2, mistral, or any Ollama-compatible model
        </p>
      </div>

      <div class="ollama-instructions">
        <h4>Setup Instructions:</h4>
        <ol>
          <li>Download and install Ollama from <a href="https://ollama.ai" target="_blank" rel="noopener">ollama.ai</a></li>
          <li>Open terminal and run: <code>ollama pull llama3</code></li>
          <li>Verify Ollama is running: <code>ollama list</code></li>
          <li>Click "Test Connection" below</li>
        </ol>
      </div>
    {:else}
      <!-- Cloud provider (Anthropic/OpenAI) -->
      <div class="form-group">
        <label for="api-key">
          API Key
          {#if config?.apiKeyConfigured}
            <span class="configured-badge">Configured: {config.maskedApiKey}</span>
          {/if}
        </label>
        <div class="api-key-input">
          <input
            id="api-key"
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            oninput={(e) => onApiKeyChange(e.currentTarget.value)}
            placeholder={config?.apiKeyConfigured ? 'Leave blank to keep existing key' : 'Enter your API key'}
            class="input"
          />
          <button
            type="button"
            class="toggle-visibility"
            onclick={onShowApiKeyToggle}
          >
            {showApiKey ? '🙈' : '👁️'}
          </button>
        </div>
        <p class="field-hint">
          {#if selectedProvider === 'anthropic'}
            Get your API key from <a href="https://console.anthropic.com/" target="_blank" rel="noopener">Anthropic Console</a>
          {:else}
            Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener">OpenAI Platform</a>
          {/if}
        </p>
      </div>

      <div class="form-group">
        <FormInput
          label="Model"
          type="text"
          value={model}
          onchange={(e) => onModelChange(e.target.value)}
          placeholder={selectedProvider === 'anthropic' ? 'claude-3-haiku-20240307' : 'gpt-3.5-turbo'}
        />
        <p class="field-hint">
          {#if selectedProvider === 'anthropic'}
            Recommended: claude-3-haiku-20240307 (fast and cost-effective)
          {:else}
            Recommended: gpt-3.5-turbo or gpt-4o-mini
          {/if}
        </p>
      </div>

      <!-- Privacy Opt-in -->
      <div class="privacy-notice">
        <div class="privacy-warning">
          <strong>⚠️ Privacy Notice</strong>
          <p>
            Using cloud AI providers will send your transaction descriptions to third-party servers
            ({selectedProvider === 'anthropic' ? 'Anthropic' : 'OpenAI'}).
            Your data will be processed securely but will leave your device.
            For maximum privacy, use local Ollama models.
          </p>
        </div>

        <FormCheckbox
          label="I understand and consent to sending transaction data to cloud AI services"
          checked={optedIntoCloud}
          onchange={(e) => onOptedIntoCloudChange(e.target.checked)}
        />
      </div>
    {/if}

    <!-- Test Connection -->
    <div class="test-connection">
      <Button
        variant="secondary"
        onclick={onTestConnection}
        disabled={testing || !enabled}
      >
        {testing ? 'Testing...' : 'Test Connection'}
      </Button>

      {#if testResult}
        <div class="test-result" class:success={testResult.success} class:error={!testResult.success}>
          {testResult.success ? '✓' : '✗'}
          {testResult.message || testResult.error}
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  .settings-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-xl);
    margin-bottom: var(--spacing-2xl);

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .settings-card {
    padding: var(--spacing-lg);
    background: var(--color-background-secondary);
    border-radius: var(--border-radius-md);
    border: var(--border-width-thin) solid var(--color-border);

    h3 {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      margin-bottom: var(--spacing-md);
      color: var(--color-text-primary);
    }
  }

  .form-group {
    margin-bottom: var(--spacing-lg);

    label {
      display: block;
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-sm);
    }

    .configured-badge {
      font-size: var(--font-size-xs);
      color: var(--color-success);
      font-weight: var(--font-weight-normal);
      margin-left: var(--spacing-sm);
    }
  }

  .api-key-input {
    display: flex;
    gap: var(--spacing-xs);

    .input {
      flex: 1;
      padding: var(--spacing-sm) var(--spacing-md);
      border: var(--border-width-thin) solid var(--color-border);
      border-radius: var(--border-radius-sm);
      font-size: var(--font-size-base);
      font-family: monospace;

      &:focus {
        outline: none;
        border-color: var(--color-primary);
      }
    }

    .toggle-visibility {
      padding: var(--spacing-sm) var(--spacing-md);
      border: var(--border-width-thin) solid var(--color-border);
      border-radius: var(--border-radius-sm);
      background: var(--color-background-secondary);
      cursor: pointer;
      font-size: var(--font-size-lg);

      &:hover {
        background: var(--color-background-hover);
      }
    }
  }

  .field-hint {
    font-size: var(--font-size-xs);
    color: var(--color-text-tertiary);
    margin-top: var(--spacing-xs);
    line-height: var(--line-height-normal);

    a {
      color: var(--color-primary);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .ollama-instructions {
    padding: var(--spacing-md);
    background: var(--color-background-secondary);
    border-radius: var(--border-radius-md);
    margin-top: var(--spacing-lg);

    ol {
      margin: var(--spacing-sm) 0 0 var(--spacing-md);

      li {
        margin-bottom: var(--spacing-xs);
        color: var(--color-text-secondary);
        font-size: var(--font-size-sm);
      }
    }

    code {
      background: var(--color-background-primary);
      padding: 2px var(--spacing-xs);
      border-radius: var(--border-radius-sm);
      font-family: monospace;
      font-size: var(--font-size-xs);
    }

    a {
      color: var(--color-primary);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .privacy-notice {
    margin-top: var(--spacing-lg);
    padding: var(--spacing-lg);
    background: var(--color-warning-light);
    border: var(--border-width-thin) solid var(--color-warning, var(--color-warning));
    border-radius: var(--border-radius-md);

    .privacy-warning {
      margin-bottom: var(--spacing-md);

      strong {
        display: block;
        color: var(--color-warning, var(--color-warning));
        margin-bottom: var(--spacing-xs);
      }

      p {
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
        line-height: var(--line-height-relaxed);
      }
    }
  }

  .test-connection {
    margin-top: var(--spacing-lg);
    padding-top: var(--spacing-lg);
    border-top: var(--border-width-thin) solid var(--color-border);
  }

  .test-result {
    margin-top: var(--spacing-md);
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--border-radius-sm);
    font-size: var(--font-size-sm);

    &.success {
      background: var(--color-success-background, var(--color-success-light));
      color: var(--color-success, var(--color-success));
      border: var(--border-width-thin) solid var(--color-success, var(--color-success));
    }

    &.error {
      background: var(--color-error-background, var(--color-danger-light));
      color: var(--color-error, var(--color-danger-hover));
      border: var(--border-width-thin) solid var(--color-error, var(--color-danger-hover));
    }
  }
</style>
